export interface InsightVersion {
  modified: string;
  size_bytes: number;
  version_id: string;
  status?: string;
  word_count?: number;
  version?: number;
}

export interface InsightMetadata {
  document_type_id: string;
  name: string;
  category: string;
  description: string;
  status: 'processing' | 'completed' | 'failed' | 'queued' | 'not_generated';
  generated_at: string | null;
  updated_at?: string;
  word_count: number;
  version: number;
  finalized?: boolean;
  finalized_at?: string;
  error_message?: string | null;
  allVersions?: InsightVersion[];
}

export interface MergedInsightItem extends Omit<InsightMetadata, 'allVersions'> {
  insight_type_id: string; // Alias for document_type_id for compatibility
  latestVersion: InsightMetadata | null;
  allVersions: InsightVersion[];
  finalized?: boolean;
  finalized_at?: string;
  isPonsCreated: boolean;
}

/**
 * Transform raw API response items to MergedInsightItem for display
 */
export const transformInsightItem = (raw: InsightMetadata): MergedInsightItem => {
  const allVersions = (raw.allVersions || []).map((v, idx) => ({
    ...v,
    version: idx + 1, // Calculate version number from index
    modified: v.modified,
    size_bytes: v.size_bytes,
    version_id: v.version_id
  }));
  
  // The current/latest version is the item itself if it has been generated
  const latestVersion: InsightMetadata | null = 
    raw.status !== 'not_generated' && raw.generated_at
      ? {
          document_type_id: raw.document_type_id,
          name: raw.name,
          category: raw.category,
          description: raw.description,
          status: raw.status,
          generated_at: raw.generated_at,
          updated_at: raw.updated_at,
          word_count: raw.word_count,
          version: raw.version,
          error_message: raw.error_message || null
        }
      : null;

  return {
    ...raw,
    insight_type_id: raw.document_type_id, // Map document_type_id as insight_type_id for compatibility
    latestVersion,
    allVersions,
    isPonsCreated: true
  };
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

export const formatCategoryName = (category: string): string => {
  return category
    .replace(/_/g, ' ')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const getStatusColor = (status: string): 'green' | 'blue' | 'red' | 'gray' | 'yellow' => {
  switch (status) {
    case 'completed':
      return 'green';
    case 'processing':
    case 'queued':
      return 'blue';
    case 'failed':
      return 'red';
    case 'available':
    case 'not_generated':
      return 'gray';
    default:
      return 'gray';
  }
};

export const getStatusLabel = (status: string, t: (key: string) => string): string => {
  switch (status) {
    case 'completed':
      return t('Completed');
    case 'processing':
      return t('Processing');
    case 'queued':
      return t('Queued');
    case 'failed':
      return t('Failed');
    case 'available':
      return t('Available');
    case 'not_generated':
      return t('Not Generated');
    default:
      return status;
  }
};
